import React, { Fragment } from 'react';
import { func, shape, bool } from 'prop-types';
import classnames from 'classnames';
import {
  notRequiredAddressSchema as addressSchema,
  getCountryFromIso,
} from 'components/Address/util';
import Spinner from '@johnlewispartnership/wtr-ingredients/ingredients/Spinner';
import { SuccessSolid, Warning } from '@johnlewispartnership/wtr-ingredients/foundations/icons';
import { MyWaitroseCard } from '@johnlewispartnership/wtr-style-dictionary/icons/react/Cards';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import { Badge } from '@johnlewispartnership/wtr-ingredients/ingredients/Badge';
import { TextLink } from '@johnlewispartnership/wtr-ingredients/ingredients/TextLink';

import styles from './AddressTile.scss';

const addressTileDefaultPropChooseAddress = () => {};
const addressTileDefaultPropOnAfterSelect = () => {};
const addressTileDefaultPropOnEdit = () => {};
const addressTileDefaultPropShowModal = () => {};
const addressTileDefaultPropShowSaveAsContactAddressModal = () => {};

const AddressTile = ({
  address,
  chooseAddress = addressTileDefaultPropChooseAddress,
  inError = false,
  isEditable = true,
  onAfterSelect = addressTileDefaultPropOnAfterSelect,
  onEdit = addressTileDefaultPropOnEdit,
  optionsLoading = true,
  selectable = false,
  canSelectContactAddress = false,
  deletable = true,
  showContactNumber = true,
  showModal = addressTileDefaultPropShowModal,
  showSaveAsContactAddressModal = addressTileDefaultPropShowSaveAsContactAddressModal,
  ignoreMyWaitroseAddressCustomizations = false,
  ignoreContactAddressCustomizations = false,
}) => {
  const {
    selected = false,
    myWaitrose = false,
    line1,
    line2,
    line3,
    town,
    region,
    country,
    postalCode,
    addressee,
    isContactAddress,
  } = address;

  const buildAddressName = () => {
    if (addressee && addressee.firstName && addressee.lastName) {
      const capitalize = nameItem => `${nameItem.charAt(0).toUpperCase()}${nameItem.slice(1)}`;
      return `${addressee.title ? `${capitalize(addressee.title)} ` : ''}${capitalize(
        addressee.firstName,
      )} ${capitalize(addressee.lastName)}`;
    }
    return null;
  };

  const contactNumber = (addressee && addressee.contactNumber) || '';
  const countryName = getCountryFromIso(country);
  const inErrorProps = {
    ...(inError && {
      'aria-invalid': true,
      'aria-errormessage': 'address-error',
      'aria-live': 'polite',
    }),
  };
  const isLoading = !inError && optionsLoading;

  return (
    <div
      aria-label={
        (myWaitrose && !ignoreMyWaitroseAddressCustomizations && 'myWaitrose associated address') ||
        (selected ? `${line1} - selected` : `${line1}`)
      }
      className={classnames(styles.addressTile, {
        [styles.addressTileSelected]: selected,
        [styles.addressTileError]: inError,
      })}
      role="article"
      data-test={inError ? 'address-tile-error' : 'address-tile'}
      {...inErrorProps}
    >
      {inError && (
        <p id="address-error" aria-live="assertive" className="sr-only">
          This address is not valid
        </p>
      )}
      <dl className={styles.addressTileDetails}>
        {isContactAddress && !ignoreContactAddressCustomizations && (
          <>
            <dt className="sr-only" />
            <dd>
              <Badge
                text="Contact address"
                data-testid="selected-contact-address-badge"
                className={styles.badgeContactAddress}
              />
            </dd>
          </>
        )}
        <dt className="sr-only">Name</dt>
        <Typography
          element="dd"
          styleAs="paragraphHeading"
          textStyle="heading-l"
          data-cs-mask
          data-testid="addressTileAddressee"
        >
          {buildAddressName(addressee)} <br />
        </Typography>
        <dt className="sr-only">Address</dt>
        <Typography element="dd" data-cs-mask data-testid="addressTileAddress">
          {line1 && (
            <Fragment>
              {line1} <br />
            </Fragment>
          )}
          {line2 && (
            <Fragment>
              {line2} <br />
            </Fragment>
          )}
          {line3 && (
            <Fragment>
              {line3} <br />
            </Fragment>
          )}
          {town && (
            <Fragment>
              {town} <br />
            </Fragment>
          )}
          {region && (
            <Fragment>
              {region} <br />
            </Fragment>
          )}
          {countryName && (
            <Fragment>
              {countryName} <br />
            </Fragment>
          )}
          <Fragment>
            {postalCode} <br />
          </Fragment>
        </Typography>
        {showContactNumber && contactNumber && (
          <Fragment>
            <Typography element="dt" styleAs="paragraphHeading" textStyle="heading-l">
              Contact number
            </Typography>
            <Typography element="dd" data-cs-mask data-testid="addressTileContactNumber">
              {contactNumber}
            </Typography>
          </Fragment>
        )}
        <div className={styles.buttonsContainer}>
          {selectable && (
            <dd>
              <TextLink
                className={styles.buttonAsLink}
                component="button"
                disabled={isLoading}
                data-testid="clickable-contact"
                onClick={() => {
                  chooseAddress(address);
                  onAfterSelect(address);
                }}
              >
                <Typography component="span">Select address</Typography>
              </TextLink>
            </dd>
          )}
          {canSelectContactAddress && !isContactAddress && (
            <dd>
              <TextLink
                className={styles.buttonAsLink}
                component="button"
                disabled={isLoading}
                onClick={() => {
                  showSaveAsContactAddressModal(address);
                }}
                data-testid="save-as-contact-address"
              >
                <Typography component="span">Save as contact address</Typography>
              </TextLink>
            </dd>
          )}
          {isEditable && (
            <Fragment>
              <dd>
                <TextLink
                  className={styles.buttonAsLink}
                  component="button"
                  disabled={isLoading}
                  onClick={() => onEdit(address)}
                  data-testid="edit-address"
                >
                  <Typography component="span">Edit</Typography>
                </TextLink>
              </dd>
              {!myWaitrose && deletable && !isContactAddress && (
                <dd>
                  <TextLink
                    className={styles.buttonAsLink}
                    component="button"
                    disabled={isLoading}
                    onClick={() => {
                      showModal(address);
                    }}
                    data-testid="delete-address"
                  >
                    <Typography component="span">Delete</Typography>
                  </TextLink>
                </dd>
              )}
            </Fragment>
          )}
        </div>
      </dl>
      {inError && <Warning className={styles.inError} data-testid="addressTileWarningIcon" />}
      {selected && <SuccessSolid className={styles.selectedIcon} />}
      <div className={styles.loading}>
        <Spinner isActive={isLoading} size="small" ariaTextActive="Please wait." />
      </div>
      {myWaitrose && !ignoreMyWaitroseAddressCustomizations && (
        <div
          className={classnames(styles.myWaitroseIcon, {
            [styles.gapToRight]: inError || selected || isLoading,
          })}
          data-test="my-waitrose-tile-icon"
        >
          <MyWaitroseCard />
        </div>
      )}
    </div>
  );
};

AddressTile.propTypes = {
  address: shape(addressSchema).isRequired,
  chooseAddress: func,
  inError: bool,
  isEditable: bool,
  onAfterSelect: func,
  onEdit: func,
  optionsLoading: bool,
  selectable: bool,
  canSelectContactAddress: bool,
  deletable: bool,
  showContactNumber: bool,
  showModal: func,
  showSaveAsContactAddressModal: func,
  ignoreMyWaitroseAddressCustomizations: bool,
  ignoreContactAddressCustomizations: bool,
};

export default AddressTile;
